import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Extension } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import tippy from 'tippy.js';
import {
  FiType,
  FiList,
  FiImage,
  FiYoutube,
  FiTable,
  FiMinus,
  FiAlertCircle,
  FiHelpCircle,
  FiCheckSquare,
  FiZap,
  FiClock,
  FiThumbsUp,
  FiThumbsDown,
  FiBarChart2,
  FiLayers,
  FiMessageSquare,
  FiCode,
} from 'react-icons/fi';
import {
  RiH1,
  RiH2,
  RiH3,
  RiListOrdered,
  RiDoubleQuotesL,
} from 'react-icons/ri';
import './SlashMenu.css';

const SLASH_COMMANDS = [
  {
    title: 'Heading 1',
    description: 'Big section heading',
    icon: <RiH1 size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleHeading({ level: 1 }).run();
    },
    category: 'Basic',
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    icon: <RiH2 size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    },
    category: 'Basic',
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    icon: <RiH3 size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleHeading({ level: 3 }).run();
    },
    category: 'Basic',
  },
  {
    title: 'Paragraph',
    description: 'Plain text',
    icon: <FiType size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().setParagraph().run();
    },
    category: 'Basic',
  },
  {
    title: 'Bullet List',
    description: 'Simple bullet list',
    icon: <FiList size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleBulletList().run();
    },
    category: 'Basic',
  },
  {
    title: 'Numbered List',
    description: 'List with numbers',
    icon: <RiListOrdered size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleOrderedList().run();
    },
    category: 'Basic',
  },
  {
    title: 'Task List',
    description: 'Todo checklist',
    icon: <FiCheckSquare size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleTaskList().run();
    },
    category: 'Basic',
  },
  {
    title: 'Quote',
    description: 'Blockquote',
    icon: <RiDoubleQuotesL size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleBlockquote().run();
    },
    category: 'Basic',
  },
  {
    title: 'Code Block',
    description: 'Code with syntax highlighting',
    icon: <FiCode size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().toggleCodeBlock().run();
    },
    category: 'Basic',
  },
  {
    title: 'Divider',
    description: 'Horizontal line',
    icon: <FiMinus size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().setHorizontalRule().run();
    },
    category: 'Basic',
  },
  {
    title: 'Image',
    description: 'Upload or embed image',
    icon: <FiImage size={18} />,
    command: ({ editor }) => {
      const url = prompt('Enter image URL:');
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    },
    category: 'Media',
  },
  {
    title: 'YouTube',
    description: 'Embed YouTube video',
    icon: <FiYoutube size={18} />,
    command: ({ editor }) => {
      const url = prompt('Enter YouTube URL:');
      if (url) {
        editor.chain().focus().setYoutubeVideo({ src: url }).run();
      }
    },
    category: 'Media',
  },
  {
    title: 'Table',
    description: 'Insert table',
    icon: <FiTable size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    },
    category: 'Media',
  },
  {
    title: 'FAQ',
    description: 'Question and answer block',
    icon: <FiHelpCircle size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertFAQ().run();
    },
    category: 'Blocks',
  },
  {
    title: 'Accordion',
    description: 'Collapsible sections',
    icon: <FiLayers size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertAccordion().run();
    },
    category: 'Blocks',
  },
  {
    title: 'Alert',
    description: 'Info, warning, error box',
    icon: <FiAlertCircle size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertAlert({ type: 'info' }).run();
    },
    category: 'Blocks',
  },
  {
    title: 'CTA',
    description: 'Call to action button',
    icon: <FiZap size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertCTA().run();
    },
    category: 'Blocks',
  },
  {
    title: 'Steps',
    description: 'Step by step guide',
    icon: <FiClock size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertSteps().run();
    },
    category: 'Blocks',
  },
  {
    title: 'Pros & Cons',
    description: 'Compare advantages and disadvantages',
    icon: <FiThumbsUp size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertProsCons().run();
    },
    category: 'Blocks',
  },
  {
    title: 'Stats',
    description: 'Display statistics',
    icon: <FiBarChart2 size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertStats().run();
    },
    category: 'Blocks',
  },
  {
    title: 'Card',
    description: 'Content card with image',
    icon: <FiLayers size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertCard().run();
    },
    category: 'Blocks',
  },
  {
    title: 'Callout',
    description: 'Highlighted note',
    icon: <FiMessageSquare size={18} />,
    command: ({ editor }) => {
      editor.chain().focus().insertCallout().run();
    },
    category: 'Blocks',
  },
];

const CommandsList = React.forwardRef((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollRef = useRef(null);

  const selectItem = (index) => {
    const item = props.items[index];
    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex((prevIndex) =>
      prevIndex === 0 ? props.items.length - 1 : prevIndex - 1
    );
  };

  const downHandler = () => {
    setSelectedIndex((prevIndex) =>
      prevIndex === props.items.length - 1 ? 0 : prevIndex + 1
    );
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useEffect(() => {
    const selectedElement = scrollRef.current?.querySelector('.slash-item.selected');
    selectedElement?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  // Group items by category
  const groupedItems = props.items.reduce((acc, item, index) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push({ ...item, globalIndex: index });
    return acc;
  }, {});

  let globalIndex = -1;

  return (
    <div className="slash-menu" ref={scrollRef}>
      {props.items.length === 0 ? (
        <div className="slash-empty">No commands found</div>
      ) : (
        Object.entries(groupedItems).map(([category, items]) => (
          <div key={category} className="slash-category">
            <div className="slash-category-title">{category}</div>
            {items.map((item) => {
              globalIndex++;
              const currentIndex = globalIndex;
              return (
                <button
                  key={item.title}
                  className={`slash-item ${selectedIndex === currentIndex ? 'selected' : ''}`}
                  onClick={() => selectItem(currentIndex)}
                  onMouseEnter={() => setSelectedIndex(currentIndex)}
                >
                  <span className="slash-item-icon">{item.icon}</span>
                  <div className="slash-item-content">
                    <span className="slash-item-title">{item.title}</span>
                    <span className="slash-item-description">{item.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
});

CommandsList.displayName = 'CommandsList';

const SlashMenu = ({ editor }) => {
  // This component doesn't render anything - it sets up the suggestion functionality
  // The actual menu is rendered by tippy via the suggestion plugin
  return null;
};

// Suggestion plugin configuration
export const SlashMenuExtension = Extension.create({
  name: 'slashMenu',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }) => {
          props.command({ editor, range });
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        items: ({ query }) => {
          return SLASH_COMMANDS.filter((item) =>
            item.title.toLowerCase().includes(query.toLowerCase()) ||
            item.description.toLowerCase().includes(query.toLowerCase())
          ).slice(0, 15);
        },
        render: () => {
          let component;
          let popup;

          return {
            onStart: (props) => {
              component = new ReactRenderer(CommandsList, {
                props,
                editor: props.editor,
              });

              if (!props.clientRect) {
                return;
              }

              popup = tippy('body', {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
              });
            },

            onUpdate: (props) => {
              component.updateProps(props);

              if (!props.clientRect) {
                return;
              }

              popup[0].setProps({
                getReferenceClientRect: props.clientRect,
              });
            },

            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                popup[0].hide();
                return true;
              }

              return component.ref?.onKeyDown(props);
            },

            onExit: () => {
              popup[0].destroy();
              component.destroy();
            },
          };
        },
      }),
    ];
  },
});

export default SlashMenu;
